import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  People as PeopleIcon,
  Restaurant as RestaurantIcon,
  CalendarToday as CalendarTodayIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI, mealsAPI, reservationsAPI, menuItemsAPI } from '../services/api';
import { toJalaali, toGregorian } from 'jalaali-js';
import { toPersianNumber } from '../utils/persianNumber';

interface ChartDataItem {
  date: string;
  persianDate: string;
  lunch: number;
  dinner: number;
}


const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, value }: any) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (value === 0) return null;

  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" style={{ fontSize: '10px', fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>
      {toPersianNumber(value)}
    </text>
  );
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalMeals: 0,
    totalReservations: 0,
    todayReservations: 0,
  });
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);
  const [genderData, setGenderData] = useState<{ name: string; value: number }[]>([]);
  const [workShiftData, setWorkShiftData] = useState<{ name: string; value: number }[]>([]);
  const [companyData, setCompanyData] = useState<{ name: string; value: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPersianMonthName, setCurrentPersianMonthName] = useState('');

  useEffect(() => {
    const loadStats = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Get dates for the current Jalali month
        const today = new Date();
        const jToday = toJalaali(today.getFullYear(), today.getMonth() + 1, today.getDate());
        const jYear = jToday.jy;
        const jMonth = jToday.jm;
        const daysInMonth = jMonth <= 6 ? 31 : (jMonth <= 11 ? 30 : (((jYear + 2346) % 128) < 30 ? 30 : 29));
        
        const formatGDate = (gy: number, gm: number, gd: number) => `${gy}-${String(gm).padStart(2, '0')}-${String(gd).padStart(2, '0')}`;
        
        const firstDayG = toGregorian(jYear, jMonth, 1);
        const lastDayG = toGregorian(jYear, jMonth, daysInMonth);
        
        const startDateStr = formatGDate(firstDayG.gy, firstDayG.gm, firstDayG.gd);
        const endDateStr = formatGDate(lastDayG.gy, lastDayG.gm, lastDayG.gd);

        const persianMonths = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];
        setCurrentPersianMonthName(persianMonths[jMonth - 1]);

        if (user.role === 'admin' || (user.role === 'manager' && user.permissions?.includes('reports'))) {
          const [users, meals, reservations, menuItems] = await Promise.all([
            user.role === 'admin' || user.permissions?.includes('users') ? usersAPI.getUsers('staff') : Promise.resolve([]),
            mealsAPI.getMeals(),
            reservationsAPI.getReservations(),
            menuItemsAPI.getMenuItems(startDateStr, endDateStr),
          ]);
          const activeUsers = users.filter((u: any) => u.isActive);
          setStats({
            totalUsers: activeUsers.length,
            totalMeals: meals.length,
            totalReservations: reservations.length,
            todayReservations: reservations.filter((r: any) => {
              const todayStr = new Date().toISOString().split('T')[0];
              return r.mealDate === todayStr;
            }).length,
          });

          // Prepare chart data map structure
          const chartDataMap: { [key: string]: ChartDataItem } = {};
          
          for (let i = 1; i <= daysInMonth; i++) {
            const gDate = toGregorian(jYear, jMonth, i);
            const dateStr = formatGDate(gDate.gy, gDate.gm, gDate.gd);
            const persianDate = `${i} ${persianMonths[jMonth - 1]}`;

            chartDataMap[dateStr] = {
              date: dateStr,
              persianDate,
              lunch: 0,
              dinner: 0,
            };
          }

          // Fill in reservation counts from menu items
          menuItems.forEach((item: any) => {
            const itemDate = item.date.split('T')[0];
            if (chartDataMap[itemDate]) {
              if (item.mealType === 'lunch') {
                chartDataMap[itemDate].lunch = item.currentReservations || 0;
              } else if (item.mealType === 'dinner') {
                chartDataMap[itemDate].dinner = item.currentReservations || 0;
              }
            }
          });

          setChartData(Object.values(chartDataMap));

          // Prepare gender chart data
          const genderCount: { [key: string]: number } = {
            'آقا': 0,
            'خانم': 0,
          };
          users.forEach((u: any) => {
            const gender = u.gender?.toLowerCase().trim() || '';
            if (gender === 'm' || gender === 'male') {
              genderCount['آقا'] = (genderCount['آقا'] || 0) + 1;
            } else if (gender === 'f' || gender === 'female') {
              genderCount['خانم'] = (genderCount['خانم'] || 0) + 1;
            }
          });
          setGenderData([
            { name: 'آقا', value: genderCount['آقا'] },
            { name: 'خانم', value: genderCount['خانم'] },
          ]);

          // Prepare work shift chart data
          const workShiftCount: { [key: string]: number } = {};
          users.forEach((u: any) => {
            const shift = u.workShift || 'day';
            workShiftCount[shift] = (workShiftCount[shift] || 0) + 1;
          });
          setWorkShiftData([
            { name: 'روز', value: workShiftCount['day'] || 0 },
            { name: 'شب', value: workShiftCount['night'] || 0 },
          ]);

          // Prepare company chart data
          const companyCount: { [key: string]: number } = {};
          users.forEach((u: any) => {
            const company = u.company || 'نامشخص';
            companyCount[company] = (companyCount[company] || 0) + 1;
          });
          const companyChartData = Object.entries(companyCount)
            .map(([name, value]) => ({ name, value }))
            .filter(item => item.name !== 'نامشخص')
            .sort((a, b) => b.value - a.value)
            .slice(0, 10); // Top 10 companies
          setCompanyData(companyChartData);
        } else if (user.role === 'manager') {
          const [meals, reservations, menuItems] = await Promise.all([
            mealsAPI.getMeals(),
            reservationsAPI.getReservations(),
            menuItemsAPI.getMenuItems(startDateStr, endDateStr),
          ]);
          setStats({
            totalUsers: 0,
            totalMeals: meals.length,
            totalReservations: reservations.length,
            todayReservations: reservations.filter((r: any) => {
              const todayStr = new Date().toISOString().split('T')[0];
              return r.mealDate === todayStr;
            }).length,
          });

          // Prepare chart data map structure
          const chartDataMap: { [key: string]: ChartDataItem } = {};
          
          for (let i = 1; i <= daysInMonth; i++) {
            const gDate = toGregorian(jYear, jMonth, i);
            const dateStr = formatGDate(gDate.gy, gDate.gm, gDate.gd);
            const persianDate = `${i} ${persianMonths[jMonth - 1]}`;

            chartDataMap[dateStr] = {
              date: dateStr,
              persianDate,
              lunch: 0,
              dinner: 0,
            };
          }

          // Fill in reservation counts from menu items
          menuItems.forEach((item: any) => {
            const itemDate = item.date.split('T')[0];
            if (chartDataMap[itemDate]) {
              if (item.mealType === 'lunch') {
                chartDataMap[itemDate].lunch = item.currentReservations || 0;
              } else if (item.mealType === 'dinner') {
                chartDataMap[itemDate].dinner = item.currentReservations || 0;
              }
            }
          });

          setChartData(Object.values(chartDataMap));
        }
      } catch (error: any) {
        console.error('Error loading stats:', error);
        if (error.response?.status !== 401) {
          setLoading(false);
        }
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [user]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const statCards = [
    ...(user?.role === 'admin' || (user?.role === 'manager' && user?.permissions?.includes('users')) ? [{
      title: 'پرسنل فعال',
      value: stats.totalUsers,
      icon: <PeopleIcon />,
      gradient: 'linear-gradient(-135deg, #5b7cfa 0%, #4a6ce8 50%, #3d5bd6 100%)',
    }] : []),
    {
      title: 'کل غذاها',
      value: stats.totalMeals,
      icon: <RestaurantIcon />,
      gradient: 'linear-gradient(-135deg, #6b8aff 0%, #5b7cfa 50%, #4a6ce8 100%)',
    },
    {
      title: 'کل رزروها',
      value: stats.totalReservations,
      icon: <CalendarTodayIcon />,
      gradient: 'linear-gradient(-135deg, #7b9aff 0%, #6b8aff 50%, #5b7cfa 100%)',
    },
    {
      title: 'رزروهای امروز',
      value: stats.todayReservations,
      icon: <TodayIcon />,
      gradient: 'linear-gradient(-135deg, #8baaff 0%, #7b9aff 50%, #6b8aff 100%)',
    },
  ];

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'Vazir, sans-serif',
            fontWeight: 800,
            mb: 1,
            background: 'linear-gradient(45deg, #fff 30%, #a5b4fc 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 10px 20px rgba(0,0,0,0.1)'
          }}
        >
          خوش آمدید، {user?.firstName} {user?.lastName}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontFamily: 'Vazir, sans-serif',
            color: 'rgba(255,255,255,0.7)',
            fontWeight: 400
          }}
        >
          به داشبورد مدیریت خوش آمدید
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 3,
          mb: 4,
        }}
      >
        {statCards.map((stat, index) => (
          <Card
            key={index}
            elevation={0}
            sx={{
              height: '100%',
              background: 'transparent',
              backdropFilter: 'none',
              border: '1px solid #fff',
              borderRadius: '15px',
              transition: 'all 0.3s ease-in-out',
              overflow: 'visible',
              position: 'relative',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 15px 30px -5px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '& .icon-box': {
                  transform: 'scale(1.1) rotate(5deg)',
                }
              },
            }}
          >
            <CardContent sx={{ p: 3, position: 'relative' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ flexGrow: 1, zIndex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'Vazir, sans-serif',
                      fontWeight: 500,
                      mb: 1,
                      color: 'rgba(255,255,255,0.7)'
                    }}
                  >
                    {stat.title}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: 'Vazir, sans-serif',
                      fontWeight: 800,
                      color: '#fff',
                      mb: 0.5,
                      textShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  >
                    {toPersianNumber(stat.value)}
                  </Typography>
                </Box>
                <Box
                  className="icon-box"
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'transparent',
                    boxShadow: 'none',
                    color: '#fff',
                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  }}
                >
                  {/* Clone element to increase icon size */}
                  {React.cloneElement(stat.icon as any, { sx: { fontSize: 32 } })}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Donut Charts - Only for Admin or Manager with reports permission */}
      {(user?.role === 'admin' || (user?.role === 'manager' && user?.permissions?.includes('reports'))) && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 3,
            mb: 3,
          }}
        >
          {/* Gender Chart */}
          <Card
            sx={{
              background: 'transparent',
              backdropFilter: 'none',
              border: 'none',
              borderRadius: '15px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            }}
          >
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ width: '100%', position: 'relative' }}>
                <ResponsiveContainer width="100%" height={250} debounce={50}>
                  <PieChart>
                    <defs>
                      <linearGradient id="genderMale" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#5b21b6" stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="genderFemale" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ec4899" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#be185d" stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="genderUnknown" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#64748b" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#475569" stopOpacity={0.7} />
                      </linearGradient>
                    </defs>
                    <Pie
                      data={genderData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={renderCustomizedLabel}
                      labelLine={false}
                    >
                      {genderData.map((entry, index) => {
                        const gradients = ['url(#genderMale)', 'url(#genderFemale)'];
                        return <Cell key={`cell-${index}`} fill={gradients[index % gradients.length]} stroke="rgba(255,255,255,0.1)" strokeWidth={2} />;
                      })}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Vazir, sans-serif',
                  fontWeight: 700,
                  mt: 2,
                  textAlign: 'center',
                  color: '#fff'
                }}
              >
                توزیع جنسیت پرسنل
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                {genderData.map((entry, index) => {
                  const colors = ['#7c3aed', '#ec4899', '#64748b'];
                  return (
                    <Box key={`legend-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colors[index % colors.length] }} />
                      <Typography variant="body2" sx={{ color: '#9ca3af', fontFamily: 'Vazir, sans-serif', fontSize: '12px' }}>
                        {entry.name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>

          {/* Work Shift Chart */}
          <Card
            sx={{
              background: 'transparent',
              backdropFilter: 'none',
              border: 'none',
              borderRadius: '15px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            }}
          >
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ width: '100%', position: 'relative' }}>
                <ResponsiveContainer width="100%" height={250} debounce={50}>
                  <PieChart>
                    <defs>
                      <linearGradient id="shiftDay" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4ade80" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#16a34a" stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="shiftNight" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#d97706" stopOpacity={0.7} />
                      </linearGradient>
                    </defs>
                    <Pie
                      data={workShiftData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={renderCustomizedLabel}
                      labelLine={false}
                    >
                      {workShiftData.map((entry, index) => {
                        const gradients = ['url(#shiftDay)', 'url(#shiftNight)'];
                        return <Cell key={`cell-${index}`} fill={gradients[index % gradients.length]} stroke="rgba(255,255,255,0.1)" strokeWidth={2} />;
                      })}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Vazir, sans-serif',
                  fontWeight: 700,
                  mt: 2,
                  textAlign: 'center',
                  color: '#fff'
                }}
              >
                توزیع شیفت کاری
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                {workShiftData.map((entry, index) => {
                  const colors = ['#4ade80', '#f59e0b'];
                  return (
                    <Box key={`legend-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colors[index % colors.length] }} />
                      <Typography variant="body2" sx={{ color: '#9ca3af', fontFamily: 'Vazir, sans-serif', fontSize: '12px' }}>
                        {entry.name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>

          {/* Company Chart */}
          <Card
            sx={{
              background: 'transparent',
              backdropFilter: 'none',
              border: 'none',
              borderRadius: '15px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            }}
          >
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ width: '100%', position: 'relative' }}>
                <ResponsiveContainer width="100%" height={250} debounce={50}>
                  <PieChart>
                    <defs>
                      <linearGradient id="company0" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#6d28d9" stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="company1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4ade80" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#16a34a" stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="company2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#d97706" stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="company3" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ef4444" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#dc2626" stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="company4" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#5b7cfa" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="company5" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ec4899" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#be185d" stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="company6" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#0891b2" stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="company7" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#14b8a6" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#0d9488" stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="company8" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f97316" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#ea580c" stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="company9" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#84cc16" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#65a30d" stopOpacity={0.7} />
                      </linearGradient>
                    </defs>
                    <Pie
                      data={companyData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={renderCustomizedLabel}
                      labelLine={false}
                    >
                      {companyData.map((entry, index) => {
                        const gradients = [
                          'url(#company0)', 'url(#company1)', 'url(#company2)', 'url(#company3)', 'url(#company4)',
                          'url(#company5)', 'url(#company6)', 'url(#company7)', 'url(#company8)', 'url(#company9)'
                        ];
                        return <Cell key={`cell-${index}`} fill={gradients[index % gradients.length]} stroke="rgba(255,255,255,0.1)" strokeWidth={2} />;
                      })}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Vazir, sans-serif',
                  fontWeight: 700,
                  mt: 2,
                  textAlign: 'center',
                  color: '#fff'
                }}
              >
                توزیع بر اساس شرکت
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                {companyData.map((entry, index) => {
                  const colors = [
                    '#8b5cf6', '#4ade80', '#f59e0b', '#ef4444', '#5b7cfa',
                    '#ec4899', '#06b6d4', '#14b8a6', '#f97316', '#84cc16'
                  ];
                  return (
                    <Box key={`legend-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colors[index % colors.length] }} />
                      <Typography variant="body2" sx={{ color: '#9ca3af', fontFamily: 'Vazir, sans-serif', fontSize: '12px' }}>
                        {entry.name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card >
        </Box >
      )}

      {/* Reservations Chart */}
      <Card
        sx={{
          mt: 3,
          background: 'rgba(30, 41, 59, 0.4)', // Dark Slate tint
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '15px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Vazir, sans-serif',
              fontWeight: 700,
              mb: 4,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <CalendarTodayIcon sx={{ color: '#4ade80' }} />
            تعداد رزروهای {currentPersianMonthName} ماه
          </Typography>
          <Box sx={{ width: '100%', position: 'relative' }}>
            <ResponsiveContainer width="100%" height={350} debounce={50}>
              <BarChart
                data={chartData}
                margin={{
                  top: 20,
                  right: 0,
                  left: 0,
                  bottom: 5,
                }}
              >
                <XAxis
                  dataKey="persianDate"
                  tick={{ fill: '#9ca3af', fontFamily: 'Vazir, sans-serif', fontSize: 12 }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                />
                <YAxis hide />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    fontFamily: 'Vazir, sans-serif',
                  }}
                  labelStyle={{ color: '#fff', fontFamily: 'Vazir, sans-serif' }}
                  formatter={(value: number, name: string) => [
                    value,
                    name === 'lunch' ? 'ناهار' : 'شام'
                  ]}
                />
                <Legend
                  formatter={(value) => (
                    <span style={{ fontFamily: 'Vazir, sans-serif', color: '#9ca3af' }}>
                      {value === 'lunch' ? 'ناهار' : 'شام'}
                    </span>
                  )}
                />
                <defs>
                  <linearGradient id="barLunch" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#1e3a8a" stopOpacity={0.8}/>
                  </linearGradient>
                  <linearGradient id="barDinner" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#4c1d95" stopOpacity={0.8}/>
                  </linearGradient>
                </defs>
                <Bar
                  dataKey="lunch"
                  name="lunch"
                  fill="url(#barLunch)"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={50}
                >
                  <LabelList dataKey="lunch" position="top" fill="#9ca3af" fontSize={10} fontFamily="Vazir, sans-serif" formatter={(value: any) => (value && Number(value) > 0) ? toPersianNumber(value) : ''} />
                </Bar>
                <Bar
                  dataKey="dinner"
                  name="dinner"
                  fill="url(#barDinner)"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={50}
                >
                  <LabelList dataKey="dinner" position="top" fill="#9ca3af" fontSize={10} fontFamily="Vazir, sans-serif" formatter={(value: any) => (value && Number(value) > 0) ? toPersianNumber(value) : ''} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    </Box >
  );
};

export default Dashboard;
